import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { useCreateAssessment, useEditAssessment } from "../mutation";
import { getScore } from "../utils/getScore";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { raiseRiskApi } from "../api/raiseRiskApi";

export const useAssessment = ({ riskId, refetch }: { riskId: string | null, refetch: () => void }) => {

    const { getFilterOptions } = useDisplayField([]);
    const riskOptions = getFilterOptions("RISK_SCORE");

    const { data: assessmentData, isFetching: isAssessmentFetching, isPending: isAssessmentPending, refetch: refetchAssessments } = useQuery({
        queryKey: ["project-risk-assessment", riskId],
        queryFn: () => raiseRiskApi.fetchAssessments({ riskId: String(riskId) }),
        enabled: !!riskId
    });

    const createAssessment = useCreateAssessment();
    const editAssessment = useEditAssessment();

    const [editImpactStatus, setEditImpactStatus] = useState<{
        editImpactStatus: boolean
        impactId: string | null
    }>({
        editImpactStatus: false,
        impactId: null
    });

    const [impactData, setImpactData] = useState<{
        impactTitle: null | string
        impactStatus: null | string
        impactDescription: null | string
        impact: number,
        probability: number,
        impactScore: string,
        probabilityScore: string,
    }>({
        impactTitle: null,
        impactStatus: null,
        impactDescription: null,
        impact: 1,
        probability: 1,
        impactScore: 'low',
        probabilityScore: 'low',
    });

    const onSaveImpact = async () => {
        const payload = {
            "title": impactData.impactTitle,
            "description": impactData.impactDescription,
            "status": impactData.impactStatus,
            "impact": getScore(impactData.impact, riskOptions),
            "probability": getScore(impactData.probability, riskOptions),
            "impact_score": impactData.impact,
            "probability_score": impactData.probability,
            "imp_prob_score": impactData.impact * impactData.probability
        }
        if (editImpactStatus.editImpactStatus) {
            await editAssessment.mutateAsync({ riskId: String(riskId), assessmentId: editImpactStatus.impactId!, data: payload });
        } else {
            await createAssessment.mutateAsync({ riskId: String(riskId), data: payload });
        };
        await refetchAssessments();
        onClearImpact();
        refetch();
    };

    const onEditImpact = async (data: any) => {
        setEditImpactStatus({
            editImpactStatus: true,
            impactId: data.id
        });
        setImpactData({
            impactTitle: data.title,
            impactDescription: data.description,
            impactStatus: data.status,
            impact: data.impact_score,
            probability: data.probability_score,
            impactScore: data.impact,
            probabilityScore: data.probability,
        });
    };

    const onClearImpact = async () => {
        setEditImpactStatus({
            editImpactStatus: false,
            impactId: null
        });
        setImpactData({
            impactTitle: null,
            impactDescription: null,
            impactStatus: null,
            impact: 1,
            probability: 1,
            impactScore: 'low',
            probabilityScore: 'low',
        });
    };

    return {
        impactData, setImpactData,
        editImpactStatus, onSaveImpact, onEditImpact, onClearImpact,
        assessmentData: assessmentData?.data?.rows, isAssessmentFetching, isAssessmentPending, refetchAssessments,
        isSaving: createAssessment.isPending || editAssessment.isPending
    }

};